import { useState, FormEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { Sponsor, saveSponsor, uploadSponsorImage } from '../lib/api';

function generateSponsorId(): string {
  return `sponsor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

type Props = {
  open: boolean;
  sponsor: Sponsor | null;
  onClose: () => void;
  onSaved: () => void;
};

export function SponsorEditorModal({ open, sponsor, onClose, onSaved }: Props) {
  const { idToken } = useAuth();
  const [name, setName] = useState(sponsor?.name ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(sponsor?.websiteUrl ?? '');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const previewUrl = imageFile ? URL.createObjectURL(imageFile) : sponsor?.imageUrl;

  const handleClose = () => {
    setName(sponsor?.name ?? '');
    setWebsiteUrl(sponsor?.websiteUrl ?? '');
    setImageFile(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!idToken) return;
    if (!imageFile && !sponsor?.imageUrl) {
      setError('Please choose an image');
      return;
    }

    setError(null);
    setSaving(true);
    try {
      const imageUrl = imageFile ? await uploadSponsorImage(imageFile, idToken) : sponsor!.imageUrl;

      await saveSponsor(
        {
          'content-id': sponsor?.['content-id'] ?? generateSponsorId(),
          type: 'sponsor',
          name,
          websiteUrl,
          imageUrl,
        },
        idToken,
        !!sponsor
      );

      onSaved();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save sponsor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { bgcolor: 'background.paper' } } }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pt: 4, px: 4 }}>{sponsor ? 'Edit Sponsor' : 'Add Sponsor'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 4, pt: '8px !important' }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Company name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
            fullWidth
          />
          <TextField
            label="Website URL"
            type="url"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://example.com"
            required
            fullWidth
          />
          <Button variant="outlined" component="label" sx={{ borderRadius: '8px' }}>
            {imageFile ? imageFile.name : 'Choose image'}
            <input
              type="file"
              accept="image/*,.svg"
              hidden
              onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          {previewUrl && (
            <img src={previewUrl} alt="Sponsor logo preview" style={{ maxHeight: 100, objectFit: 'contain' }} />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, pt: 2 }}>
          <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disableElevation disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
